import { fireInteractionEvent } from 'api/googleTagManager'
import TrackingContext, { TrackingProps } from 'contexts/trackingContext'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

import { removeRecentlyViewedOffers } from 'actions/OfferActions'
import { productClick } from 'analytics/enhanced-ecommerce'
import { GAProductFieldObject } from 'analytics/eventDefinitions'
import trackClientEvent from 'analytics/snowplow-events/clientEvents'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import CloseButton from 'components/Common/CloseButton'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import Image from 'components/Common/Image'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import Group from 'components/Luxkit/Group'
import Pill from 'components/Luxkit/Pill'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_TOUR } from 'constants/offer'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getHotelOfferDisplayedValue } from 'lib/getOfferDisplayPricing'
import noop from 'lib/noop'

const TILE_PADDING = 8

export type HighIntentRecommendationType = 'RecentlyViewed' | 'HotLead' | 'AbandonedCart'

const imageWidth = 68
const imageHeight = 92
// on mobile everything is scaled down by 20%, so we have to enlarge the target size
const imageWidthMobile = Math.floor(imageWidth / 0.8)
const imageHeightMobile = Math.floor(imageHeight / 0.8)
const ImageBlock = styled.div`
  flex-shrink: 0;

  width: ${rem(imageWidthMobile)};
  height: ${rem(imageHeightMobile)};

  ${mediaQueryUp.sm} {
    width: ${rem(imageWidth)};
    height: ${rem(imageHeight)};
  }
`

const tileWidth = 376
const tileWidthMobile = Math.floor(320 / 0.8)
const Root = styled(ClickableLink)`
  width: ${rem(tileWidthMobile)};
  min-height: ${rem(imageHeightMobile)};

  ${mediaQueryUp.sm} {
    width: ${rem(tileWidth)};
    min-height: ${rem(imageHeight)};
  }

  align-self: start;
  background-color: ${props => props.theme.palette.neutral.eight};
  overflow: hidden;
  transition: outline 0.2s;

  // on desktop, close button is hidden unless hover
  ${mediaQueryUp.sm} {
    padding: ${rem(TILE_PADDING)};
    .high-intent-close-button {
      visibility: hidden;
    }
    &:hover {
      background-color: ${props => props.theme.palette.neutral.seven};
      .high-intent-close-button {
        visibility: visible;
      }
    }
  }
  
  &.tile-border {
    border: 1px solid ${props => props.theme.palette.neutral.five};
    border-radius: ${props => props.theme.borderRadius.small};
  }

  .input-keyboard &:focus {
    outline: 2px solid  ${props => props.theme.palette.neutral.five};
    outline-offset: 2px;
  }
`

const closeButtonLength = 24
const closeButtonLengthMobile = Math.floor(closeButtonLength / 0.8)
/** Close button */
const CloseBlock = styled(CloseButton)`
  width: ${rem(closeButtonLengthMobile)};
  height: ${rem(closeButtonLengthMobile)};
  ${mediaQueryUp.sm} {
    width: ${rem(closeButtonLength)};
    height: ${rem(closeButtonLength)};
  }
`

const validOfferTypes: Array<App.OfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_TOUR,
  'direct_tour',
  'partner_tour',
  'connection_tour',
  OFFER_TYPE_BED_BANK,
]

export function shouldDisplay(offerType: App.OfferType) {
  return validOfferTypes.includes(offerType)
}

interface BaseProps {
  category: string
  currency: string
  image: App.Image
  title: string
  name: string
  offerId: string
  offerType: App.OfferType
  position: number
  tracking: TrackingProps
  testId: string
  url: string
  occupanyText?: string
  recommendationType?: HighIntentRecommendationType,
  urgencyLabels?: App.OfferUrgencyLabel[],
  onClose?: () => void
}

interface FlightProps {
  bundledWithFlightsOnly?: boolean
  flightOrigin?: string
  flightPrices?: { [airportCode: string]: number }
}

interface PricingProps {
  duration: string
  price: number
  saleUnit: string
}

interface OfferProps {
  flights?: FlightProps
  shouldDisplayValue?: boolean
  value?: number
  walledGarden?: boolean
}

interface OfferAndTourV2Props {
  pricing?: PricingProps
}

function HighIntentRecommendationTile({
  category,
  currency,
  flights,
  image,
  title,
  name,
  offerId,
  offerType,
  occupanyText,
  position,
  pricing,
  shouldDisplayValue,
  testId,
  tracking,
  url,
  value,
  urgencyLabels,
  recommendationType = 'RecentlyViewed',
  onClose = noop,
}: BaseProps & OfferProps & OfferAndTourV2Props) {
  // ------- Render props -------------
  const { price, duration } = pricing ?? {}

  const showPrice = !!price && price > 0

  const displayValue = getHotelOfferDisplayedValue(false, price ?? 0, value ?? 1, !!shouldDisplayValue)
  const { shouldShowDiscountPercent, highestDiscountPercent } = displayValue
  const discountText = recommendationType === 'AbandonedCart' ?
      `-${highestDiscountPercent}` :
      `Save up to ${highestDiscountPercent}%`

  const durationText = duration ? (
    (recommendationType === 'AbandonedCart') ? `Total for ${duration}` : `${duration} from`
  ) : 'From'

  // when we don't have occupancy, there are extra space so the title can take 2 lines
  const titleLines = occupanyText ? 1 : 2

  // ------- Analytics -------------
  const trackingCtx = useContext(TrackingContext)

  const listId = tracking?.listId ?? 'unknown'
  const fireProductClick = useCallback(() => {
    const productClickObject: GAProductFieldObject = {
      id: offerId,
      name,
      category,
      price,
      quantity: 1,
      position,
    }
    const extras = tracking?.lere ? {
      lereVersion: tracking.lere.version,
    } : {}
    productClick(currency, productClickObject, {
      listId,
      listName: tracking?.listName ?? 'High Intent Recommendation Tile',
    }, extras)
  }, [
    category,
    currency,
    name,
    offerId,
    position,
    price,
    listId,
    tracking?.listName,
    tracking?.lere,
  ])

  const onClick = useCallback(() => {
    if (trackingCtx) {
      fireInteractionEvent({
        ...trackingCtx,
        action: 'Click',
        value: position,
      })
      fireProductClick()
    }
  }, [trackingCtx, position, fireProductClick])

  const dispatch = useAppDispatch()

  const handleDismissed = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onClose()
    trackClientEvent(`${listId} ${offerId}`, 'high_intent_tile_dismissed', 'lere', 'interaction')
    dispatch(removeRecentlyViewedOffers([offerId]))
  }, [dispatch, onClose, listId, offerId])

  if (!shouldDisplay(offerType) || !!flights?.bundledWithFlightsOnly) {
    return null
  }

  return (
    <Root to={url} onClick={onClick} target="_blank" data-testid={testId}>
      <Group direction="horizontal" fullWidth gap={12}>

        <ImageBlock>
          <Image
              id={image.id}
              alt={image.title}
              width={imageWidth}
              height={imageHeight}
              dpr={2}
              fit="center"
            />
        </ImageBlock>

        <Group direction="vertical" fullWidth gap={4}>
          <Group direction="vertical" fullHeight verticalAlign="start" gap={4}>
            <BodyText variant="medium" weight="bold" lineClamp={titleLines}>{title}</BodyText>
            {!!occupanyText && <Caption variant="large">{occupanyText}</Caption>}

            {!!urgencyLabels?.length && <OfferUrgencyLabels urgencyLabels={urgencyLabels} countDownShowHourOnly />}
          </Group>
          {(showPrice && !!durationText) && <Caption variant="large">{durationText}</Caption>}
          {showPrice &&
            <Group direction="horizontal" gap={4}>
              <BodyText variant="large" weight="bold">
                <FormatCurrency value={price} format="roundedDollar" />
              </BodyText>
              {shouldShowDiscountPercent &&
                <Pill kind="primary" variant="success">
                  {discountText}
                </Pill>
              }
            </Group>
          }
        </Group>

        <CloseBlock className="high-intent-close-button" onClick={handleDismissed} size="large"/>

      </Group>
    </Root>
  )
}

export default HighIntentRecommendationTile
